import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./RequestsBySmsProviderAndSenderTypeChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardLegendTop,
    buildMonitorDashboardResponsiveChartOptions,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
} from "../../../chart/monitorDashboardChartKit";
import { SENDERS_FILTER_KEY } from "../sendersDetailsShared";

@WithRender
@Component({
    diProvide: [RequestsSendersService],
})
export default class RequestsBySmsProviderAndSenderTypeChart extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    @Prop({ required: true }) public filter!: any;

    private chart: any = null;
    private canvasId: string = "requests-by-sms-provider-and-sender-type-chart-canvas";
    private readonly rowHeightPx: number = 32;
    private readonly legendAndPaddingPx: number = 72;
    public hasData: boolean = true;
    public isLoading: boolean = false;
    public chartPixelHeight: number = 320;
    private requestVersion: number = 0;
    public viewportWidth: number = 1440;
    private resizeHandler: any = null;

    public get scrollViewportPx(): number {
        if (this.viewportWidth >= 1600) {
            return 480;
        }

        if (this.viewportWidth >= 1200) {
            return 440;
        }

        if (this.viewportWidth >= 992) {
            return 380;
        }

        return 320;
    }

    public get scrollAreaStyle(): Record<string, string> {
        const h = String(this.scrollViewportPx);
        return {
            height: h + "px",
            maxHeight: h + "px",
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
        };
    }

    public get innerChartWrapperStyle(): Record<string, string> {
        const h = String(this.chartPixelHeight);
        const minH = String(this.scrollViewportPx);
        return {
            width: "100%",
            position: "relative",
            height: h + "px",
            minHeight: minH + "px",
        };
    }

    public mounted(): void {
        this.resizeHandler = this.onResize.bind(this);
        window.addEventListener("resize", this.resizeHandler);
        this.onResize();
    }

    public beforeDestroy(): void {
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
            this.resizeHandler = null;
        }

        this.destroyChart();
    }

    private onResize(): void {
        this.viewportWidth = window && window.innerWidth ? window.innerWidth : 1440;
        if (this.chart && typeof this.chart.resize === "function") {
            this.chart.resize();
        }
    }

    public onTitleClick(): void {
        this.navigateToDetails();
    }

    private navigateToDetails(): void {
        const f = this.filter;
        const sendersPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
        };

        try {
            sessionStorage.setItem(
                SENDERS_FILTER_KEY,
                JSON.stringify(sendersPayload),
            );
        } catch (e) {
        }

        this.$router.push({ name: "RequestsBySmsProviderAndSenderTypeDetails" });
    }

    private onChartClick(): void {
        this.navigateToDetails();
    }

    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const resp = await this.requestsSendersService.GetRequestsBySmsProviderAndSenderType(this.filter);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;
        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.destroyChart();
            return;
        }

        const rows = resp.result;
        let normalizedRows = rows.map((r: any) => ({
            providerId: r && r.providerId ? String(r.providerId) : "",
            providerName: r && r.providerName ? String(r.providerName) : "",
            advertisementCount: r && r.advertisementCount != null ? Number(r.advertisementCount) : 0,
            awarenessCount: r && r.awarenessCount != null ? Number(r.awarenessCount) : 0,
            serviceCount: r && r.serviceCount != null ? Number(r.serviceCount) : 0,
            warningCount: r && r.warningCount != null ? Number(r.warningCount) : 0,
        }));

        if (!this.isWarningSenderTypeVisible()) {
            normalizedRows = normalizedRows.map((x: any) => ({
                providerName: x.providerName,
                advertisementCount: x.advertisementCount,
                awarenessCount: x.awarenessCount,
                serviceCount: x.serviceCount,
                warningCount: 0,
            }));
        }

        let sumTotals = 0;
        for (let i = 0; i < normalizedRows.length; i++) {
            const x = normalizedRows[i];
            sumTotals +=
                x.advertisementCount +
                x.awarenessCount +
                x.serviceCount +
                x.warningCount;
        }

        const apiTotalRaw = resp && (resp as any).totalCount != null ? Number((resp as any).totalCount) : NaN;
        const totalAll = !isNaN(apiTotalRaw) ? apiTotalRaw : sumTotals;

        const barCount = normalizedRows.length > 0 ? normalizedRows.length : 1;
        this.hasData = totalAll > 0;

        if (this.hasData) {
            this.chartPixelHeight = Math.max(260, this.legendAndPaddingPx + barCount * this.rowHeightPx);
        }

        await this.$nextTick();
        this.renderChart(normalizedRows);
    }

    private renderChart(rows: any[]): void {
        this.destroyChart();
        if (!this.hasData) return;

        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) return;

        const ctx = el.getContext("2d") as any;
        const labels = rows.map((x: any) => x.providerName);
        const advertisementValues = rows.map((x: any) => x.advertisementCount);
        const awarenessValues = rows.map((x: any) => x.awarenessCount);
        const serviceValues = rows.map((x: any) => x.serviceCount);
        const warningValues = rows.map((x: any) => x.warningCount);
        const xMax = this.computeNiceXAxisMax(advertisementValues, awarenessValues, serviceValues, warningValues);
        const includeWarning = this.isWarningSenderTypeVisible();
        const fillAdvertisement = getMonitorDashboardChartColor("requestsBySmsProviderAndSenderType", "advertisement");
        const fillAwareness = getMonitorDashboardChartColor("requestsBySmsProviderAndSenderType", "awareness");
        const fillService = getMonitorDashboardChartColor("requestsBySmsProviderAndSenderType", "service");
        const fillWarning = getMonitorDashboardChartColor("requestsBySmsProviderAndSenderType", "warning");

        const fontFamily = getMonitorDashboardFontFamily();
        const baseOpts = buildMonitorDashboardResponsiveChartOptions();

        const datasets: any[] = [
            {
                label: String(this.$t("shared.advertisement")),
                data: advertisementValues,
                backgroundColor: fillAdvertisement,
                borderColor: monitorDashboardDarkerBorder(fillAdvertisement),
                borderWidth: 2,
            },
            {
                label: String(this.$t("shared.awareness")),
                data: awarenessValues,
                backgroundColor: fillAwareness,
                borderColor: monitorDashboardDarkerBorder(fillAwareness),
                borderWidth: 2,
            },
            {
                label: String(this.$t("shared.service")),
                data: serviceValues,
                backgroundColor: fillService,
                borderColor: monitorDashboardDarkerBorder(fillService),
                borderWidth: 2,
            },
        ];

        if (includeWarning) {
            datasets.push({
                label: String(this.$t("shared.warning")),
                data: warningValues,
                backgroundColor: fillWarning,
                borderColor: monitorDashboardDarkerBorder(fillWarning),
                borderWidth: 2,
            });
        }

        const self = this;
        const chartInstance = new Chart(ctx, {
            type: "horizontalBar",
            data: {
                labels: labels,
                datasets: datasets,
            },
            options: Object.assign({}, baseOpts, {
                onClick: function () {
                    self.onChartClick();
                },
                legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                max: xMax,
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            gridLines: { display: false },
                            ticks: {
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                        },
                    ],
                },
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    enabled: true,
                    mode: "index",
                    intersect: false,
                    callbacks: {
                        title: function (items: any) {
                            if (!items || !items.length) return "";
                            return String(items[0].yLabel || "");
                        },
                        label: function (item: any, data: any) {
                            const dsIndex = item.datasetIndex;
                            const idx = item.index;
                            const ds = data && data.datasets && data.datasets[dsIndex] ? data.datasets[dsIndex] : null;
                            const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                            const value = isNaN(raw) ? 0 : raw;
                            const dsLabel = ds && ds.label ? String(ds.label) : String(self.$t("shared.senders"));
                            return dsLabel + ": " + String(value);
                        },
                        afterBody: function (items: any[], data: any) {
                            let total = 0;
                            if (items && items.length) {
                                for (let i = 0; i < items.length; i++) {
                                    const item = items[i];
                                    const ds = data && data.datasets && data.datasets[item.datasetIndex]
                                        ? data.datasets[item.datasetIndex]
                                        : null;
                                    const raw = ds && ds.data && ds.data.length > item.index ? Number(ds.data[item.index]) : 0;
                                    const value = isNaN(raw) ? 0 : raw;
                                    total += value;
                                }
                            }
                            return String(self.$t("monitorDashboard.total")) + ": " + String(total);
                        },
                    },
                }),
            }),
            plugins: [
                createMonitorDashboardLegendHeightPlugin(14),
                {
                    afterDatasetsDraw: function (chart: any) {
                        const ctx2: any = chart.ctx;
                        ctx2.save();
                        ctx2.textAlign = "center";
                        ctx2.textBaseline = "middle";
                        ctx2.font = "12px " + getMonitorDashboardFontFamily();
                        ctx2.fillStyle = "#FFFFFF";

                        const datasets = chart.data && chart.data.datasets ? chart.data.datasets : [];
                        for (let di = 0; di < datasets.length; di++) {
                            const dataset = datasets[di];
                            const meta = chart.getDatasetMeta(di);
                            if (!meta || meta.hidden || !meta.data) continue;

                            for (let i = 0; i < meta.data.length; i++) {
                                const value = Number(dataset.data[i] || 0);
                                if (value <= 0) continue;

                                const bar = meta.data[i];
                                const model = bar && bar._model ? bar._model : null;
                                if (!model) continue;

                                const barWidth = Math.abs(Number(model.base) - Number(model.x));
                                if (barWidth < 18) continue;

                                const leftX = Math.min(Number(model.base), Number(model.x));
                                const centerX = leftX + (barWidth / 2);
                                ctx2.fillText(String(value), centerX, model.y);
                            }
                        }

                        ctx2.restore();
                    },
                },
            ],
        });

        this.chart = chartInstance;

        this.$nextTick().then(() => {
            if (chartInstance && typeof chartInstance.resize === "function") {
                chartInstance.resize();
            }
        });
    }

    private isWarningSenderTypeVisible(): boolean {
        const customerType =
            this.filter && this.filter.customerType != null
                ? Number(this.filter.customerType)
                : 0;

        return customerType !== 2 && customerType !== 3;
    }

    private computeNiceXAxisMax(advertisement: number[], awareness: number[], service: number[], warning: number[]): number {
        let maxVal = 0;
        for (let i = 0; i < advertisement.length; i++) {
            const total = Number(advertisement[i]) + Number(awareness[i]) + Number(service[i]) + Number(warning[i]);
            if (total > maxVal) maxVal = total;
        }

        if (maxVal <= 0) return 10;
        if (maxVal <= 20) return 20;
        if (maxVal <= 50) return Math.ceil(maxVal / 5) * 5;
        if (maxVal <= 120) return Math.ceil(maxVal / 10) * 10;
        if (maxVal <= 300) return Math.ceil(maxVal / 20) * 20;
        return Math.ceil(maxVal / 50) * 50;
    }
}
